import { useEffect, useMemo, useState } from 'react';
import { Card, DataTable, SkeletonBodyText, Text } from '@shopify/polaris';
import { getRequest } from '../../../../shared/components/functions';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { businessUnitParam } from './BusinessUnitFilter';
import { formatInteger, formatPercent, toNumber } from './analyticsHelpers';

// Customer retention and churn per year, plus where the revenue comes from
// (kept customers vs brand-new vs reactivated ones).
export default function RetentionCard({ businessUnitId = null, titleSuffix = '' }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      const result = await getRequest(`/api/metrics/customer-retention${businessUnitParam(businessUnitId, true)}`);
      if (cancelled) return;
      setData(Array.isArray(result) ? result : []);
      setLoading(false);
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [businessUnitId]);

  const rows = useMemo(() => {
    const byYear = {};
    data.forEach((row) => {
      byYear[row.year] = row;
    });

    return [...data]
      .sort((a, b) => b.year - a.year)
      .map((row) => {
        const previous = byYear[row.year - 1];
        const prevActive = toNumber(previous?.active_customers);
        const retained = toNumber(row.retained_customers);
        const churned = prevActive ? prevActive - retained : null;
        const revenue = toNumber(row.revenue_incl);
        const isCurrentYear = Number(row.year) === new Date().getFullYear();
        return [
          <b>{isCurrentYear ? `${row.year} (YTD)` : row.year}</b>,
          formatInteger(row.active_customers),
          formatInteger(row.new_customers),
          formatInteger(row.reactivated_customers),
          formatInteger(retained),
          prevActive ? formatPercent(retained / prevActive) : '—',
          churned === null ? '—' : formatInteger(churned),
          revenue ? formatPercent(toNumber(row.revenue_from_retained) / revenue) : '—',
          revenue ? formatPercent(toNumber(row.revenue_from_new) / revenue) : '—',
          revenue ? formatPercent(toNumber(row.revenue_from_reactivated) / revenue) : '—'
        ];
      });
  }, [data]);

  return (
    <Card>
      <CardHeading title={`Customer retention & churn${titleSuffix}`} />
      <Text tone="subdued" as="p">
        Per year: how many invoiced customers you kept from the year before (retained), how many were brand-new, how many came back after a gap (reactivated), and how much of the revenue each group
        brought. Retention % = retained divided by last year's active customers.
      </Text>
      <div style={{ marginTop: '12px' }}>
        {loading ? (
          <SkeletonBodyText lines={10} />
        ) : (
          <DataTable
            verticalAlign="middle"
            increasedTableDensity
            stickyHeader
            columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
            headings={[
              <b>Year</b>,
              <b>Active</b>,
              <b>New</b>,
              <b>Reactivated</b>,
              <b>Retained</b>,
              <b>Retention %</b>,
              <b>Churned</b>,
              <b>Rev. from retained</b>,
              <b>Rev. from new</b>,
              <b>Rev. from reactivated</b>
            ]}
            rows={rows}
          />
        )}
      </div>
    </Card>
  );
}
